import { get, post } from 'pc-sales/utils/http';
import client, { ClientApi } from './client';
import sales, { SalesApi } from './sales';
import archives, { ArchivesApi } from './archives';
import details, { DetailsApi } from './details';

type UserType = 'admin' | 'client' | 'sales';

// 实名认证匹配结果（薄代理 Java user域 GET /kyc/real/channel）
// http 层已解包 { code, data }，needRealName 即该用户是否需要实名（Java 出参 needRealNameVerification）
export interface RealnameMatchResult {
    needRealName: boolean;
}

export { ClientApi, SalesApi, ArchivesApi, DetailsApi };
export type { UserType };

export class UserApi {
    client = client; // 客户
    sales = sales; // 销售
    archives = archives; // 档案
    details = details; // 客户详情其它列表

    // 获取当前账户与某一账户关系
    relation = (
        data:
            | {
                  uuid: string;
              }
            | {
                  uemail: string;
              }
            | {
                  uphone: string;
              },
    ) => get('/v2/sales/manage/checkRelation', data, { ignoreError: true });

    // 获取用户详情
    info = () => get('/v2/user/sales/show', null, { ignoreError: true });

    // 修改个人资料
    update = (
        data:
            | {
                  firstname?: string;
                  lastname?: string;
                  uhead?: object;
                  address?: string;
                  usex?: 'male' | 'female';
                  birthday?: string;
                  certificate?: string[];
                  custom1?: string;
                  custom2?: string;
                  country?: string;
                  open_auth?: number;
                  nickname?: string;
              }
            | {
                  phone_country?: string;
                  uphonecode: string;
                  uphone: string;
              }
            | {
                  uemail: string;
              }
            | {
                  country: string;
              }
            | {
                  password: string;
              },
    ) => post('/v2/user/sales/update', data);

    // 退出
    signOut = () => post('/v2/user/auth/signOut');

    // 获取用户设置
    getSetting = (data: Record<string, any>) => get('/v2/user/account/getSetting', data, { ignoreError: true });

    // 设置用户设置
    setSetting = (data: Record<string, any>) => post('/v2/user/account/setSetting', data, { ignoreError: true });

    // 根据姓名匹配uuid
    match = (data: { uname: string; pagesize?: number; page?: number; fields?: string; utype?: UserType }) =>
        get('/v2/client/crm/match', data);

    // 根据uuid批量获取姓名
    listInfo = (data: { uuid_list: string[]; sales_type?: 'personal' | 'team' }) =>
        post('/v2/client/crm/listInfo', data);

    // 检测邮箱/手机是否存在
    checkExist = (
        data:
            | {
                  utype?: UserType;
                  uemail: string;
              }
            | {
                  utype?: UserType;
                  uphonecode: string;
                  uphone: string;
              },
    ) => get('/v2/user/account/checkExist', data);

    checkExistNew = async (
        data:
            | {
                  uemail: string;
              }
            | {
                  uphonecode: string;
                  uphone: string;
              },
    ) => await post('/v2/user/account/existNew', data);

    // 检测证件是否存在
    checkIdExist = (data: { idType: string; idNumber: string }) =>
        get('/v2/user/profile/checkIdExist', data, { ignoreError: true });

    /**
     * 获取ip信息
     */
    ip = () => get('/v2/common/location/ip');

    // 获取角色列表
    role = (data: {
        page: number;
        pagesize: number;
        role_name?: string;
        utype?: 'admin' | 'sales' | 'ib' | 'client';
    }) => get('/v2/user/role', data);

    // 获取标签库
    getTags = () => get('/v2/user/tags/all');

    // 新建标签
    createTags = (data: { list: string[] }) => post('/v2/user/tags/create', data);

    // 校验账户类型
    checkType = (data: { uuid: string; utype: (UserType | 'ib')[]; filter?: any }) =>
        post('/v2/user/auth/checkType', data, { ignoreError: true });

    // 重置密码
    forgetPswReset = (data: Object) => post('/v2/user/account/forgetPswReset', data);

    // 创建一条注册审核记录
    createRegAudit = (data: Object) => post('/user/createRegAudit', data);

    // todo: 获取ip详情
    getIp = () => get('/v2/common/location/ip');

    // 检查用户登录状态
    checkStatus = () => get('/v2/user/auth/show');

    // 获取 TFA 秘钥
    getTfaToken = () => get('/v2/user/tfa');

    // 绑定 TFA
    bindTfaToken = (data: { secret: string; code: string }) => post('/v2/user/tfa/create', data);

    // 解绑 TFA
    unBindTfaToken = (data: { code: string }) => post('/v2/user/tfa/destroy', data);

    // 通过其他方式解绑
    unBindTfaTokenByOtherMethod = () => post('/v2/user/tfa/destroyByOtherMethod');

    // 获取佣金冻结天数
    getFundStatus = (data: { uuid: string }) => get('/v2/user/account/getFundStatus', data);

    // 更新当前登录用户语言
    updateLanguage = (data: { language: string }) => post('/v2/user/account/updateLanguage', data);
    // 实名认证匹配：返回 { needRealName } 该用户是否需要实名（薄代理 Java user域 GET /kyc/real/channel，appid 由 Node 透传）
    realNameMatch = (uuid: string): Promise<RealnameMatchResult> => get('/v2/user/realNameMatch', { uuid });
}

export default new UserApi();
